import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  LinearProgress,
  useTheme,
  alpha,
  Collapse,
  Divider,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  Straighten as StraightenIcon,
  Inventory2 as Inventory2Icon,
  ViewList as ViewStockIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import ImageManagement from './ImageManagement';
import AddProductSizeModal from './AddProductSizeModal';
import ManageProductSizesModal from './ManageProductSizesModal';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { useToast } from 'OtherReusableFunctionality/ToastContext';
import buttonConfig from "config/ConfigAccessButton";

const ProductsList = ({ products, categories, searchQuery, selectedCategory, onRefresh, onEditProduct }) => {
  const theme = useTheme();
  const toast = useToast();
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [imageManagementOpen, setImageManagementOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [addSizeModalOpen, setAddSizeModalOpen] = useState(false);
  const [manageSizesModalOpen, setManageSizesModalOpen] = useState(false);
  const [sizeProduct, setSizeProduct] = useState(null);
  const [viewStockOpen, setViewStockOpen] = useState(false);
  const [viewStockProduct, setViewStockProduct] = useState(null);
  const [viewStockSizes, setViewStockSizes] = useState([]);
  const [viewStockEntries, setViewStockEntries] = useState([]);
  const [viewStockLoading, setViewStockLoading] = useState(false);
  const [viewStockError, setViewStockError] = useState('');

  // ============ PERMISSION FUNCTIONS - FIXED ============
  const getCurrentButtonPermissions = () => {
    const moduleConfig = buttonConfig["Product Management"];
    return moduleConfig || {};  // Return directly, no .actions property
  };
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  const filteredProducts = products;

  const handleExpandClick = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const handleImageManagement = (product) => {
    setSelectedProduct(product);
    setImageManagementOpen(true);
  };

  const handleAddSize = (product) => {
    setSizeProduct(product);
    setAddSizeModalOpen(true);
  };

  const handleManageStock = (product) => {
    setSizeProduct(product);
    setManageSizesModalOpen(true);
  };

  const getSizeListFromResponse = (response) => {
    const data = response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.sizes)) return data.sizes;
    if (Array.isArray(data?.ProductSize)) return data.ProductSize;
    if (Array.isArray(data?.product_sizes)) return data.product_sizes;
    if (Array.isArray(data?.product_size)) return data.product_size;
    return [];
  };

  const handleViewStock = async (product) => {
    setViewStockProduct(product);
    setViewStockOpen(true);
    setViewStockSizes([]);
    setViewStockEntries([]);
    setViewStockError('');
    setViewStockLoading(true);

    try {
      const [sizeResponse, stockResponse] = await Promise.all([
        apiService.get(`${API_ENDPOINTS.GET_product_size_by_id}?product_id=${product.id}`),
        apiService.get(`${API_ENDPOINTS.GET_product_stock_by_product_id}?product_id=${product.id}`)
      ]);

      const stockData = stockResponse?.data;
      const stockList = Array.isArray(stockData)
        ? stockData
        : Array.isArray(stockData?.ProductStock)
          ? stockData.ProductStock
          : Array.isArray(stockData?.product_stock)
            ? stockData.product_stock
            : [];

      setViewStockEntries(stockList);

      // Products without Add Size: show stock entries directly (no size breakdown)
      if (product.add_size !== true) {
        if (stockResponse?.success === false) {
          setViewStockError(stockResponse?.message || 'Failed to fetch available stock');
        }
        return;
      }

      if (sizeResponse?.success) {
        const sizeList = getSizeListFromResponse(sizeResponse);

        const sizesWithStock = sizeList.map((sizeItem) => {
          const sizeId = sizeItem?.id ?? sizeItem?.product_size_id ?? sizeItem?.size_id;
          const hasUnlimited = stockList.some((stockItem) => {
            const isUnlimited = stockItem?.is_unlimited === true
              || stockItem?.is_unlimited === 'true'
              || stockItem?.is_unlimited === 1;
            if (!isUnlimited) return false;

            const stockSizeId = stockItem?.product_size?.id
              ?? stockItem?.product_size_id
              ?? stockItem?.size_id
              ?? (typeof stockItem?.product_size === 'object' ? null : stockItem?.product_size);

            return stockSizeId?.toString() === sizeId?.toString()
              || stockItem?.product_size?.size?.toString() === sizeItem?.size?.toString()
              || parseInt(String(stockItem?.product_size ?? ''), 10) === parseInt(String(sizeItem?.size ?? ''), 10)
              || parseInt(String(stockItem?.size ?? ''), 10) === parseInt(String(sizeItem?.size ?? ''), 10);
          });

          return {
            ...sizeItem,
            is_unlimited: hasUnlimited || sizeItem.is_unlimited
          };
        });

        setViewStockSizes(sizesWithStock);
      } else {
        setViewStockSizes([]);
        setViewStockError(sizeResponse?.message || 'Failed to fetch available stock');
      }
    } catch (fetchError) {
      console.error('Error fetching size-wise stock:', fetchError);
      setViewStockSizes([]);
      setViewStockEntries([]);
      setViewStockError(fetchError?.message || 'An error occurred while fetching available stock');
    } finally {
      setViewStockLoading(false);
    }
  };

  const handleCloseViewStock = () => {
    setViewStockOpen(false);
    setViewStockProduct(null);
    setViewStockSizes([]);
    setViewStockEntries([]);
    setViewStockError('');
    setViewStockLoading(false);
  };

  const handleSizeUpdated = () => {
    if (onRefresh) onRefresh();
  };

  const handleOpenDeleteDialog = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setProductToDelete(null);
    setDeleteDialogOpen(false);
  };

  const getStockPercentage = (product) => {
    const remaining = parseFloat(product.remaining_quantity || 0);
    const total = parseFloat(product.analytics?.total_stock || 0);
    return total > 0 ? (remaining / total) * 100 : 0;
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
  };

  const getFirstProductSize = (product) => {
    const raw =
      product?.sizes
      ?? product?.product_sizes
      ?? product?.ProductSize
      ?? product?.product_size
      ?? [];

    let sizes = raw;
    if (typeof sizes === 'string') {
      try {
        sizes = JSON.parse(sizes);
      } catch {
        sizes = [];
      }
    }
    if (!Array.isArray(sizes) && sizes && typeof sizes === 'object') {
      sizes = Object.values(sizes);
    }
    if (!Array.isArray(sizes) || sizes.length === 0) return null;

    // First added size: lowest sequence, then lowest id
    return [...sizes].sort((a, b) => {
      const seqA = Number(a?.sequence);
      const seqB = Number(b?.sequence);
      const safeSeqA = Number.isFinite(seqA) ? seqA : Number.MAX_SAFE_INTEGER;
      const safeSeqB = Number.isFinite(seqB) ? seqB : Number.MAX_SAFE_INTEGER;
      if (safeSeqA !== safeSeqB) return safeSeqA - safeSeqB;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    })[0];
  };

  const getProductDisplayPrices = (product) => {
    const firstSize = getFirstProductSize(product);
    const farmerPrice = firstSize?.farmer_price ?? firstSize?.farmerPrice ?? product?.farmer_price;
    const mrp = firstSize?.mrp ?? firstSize?.MRP ?? product?.mrp;
    return { farmerPrice, mrp };
  };

  const getProductImage = (product) => {
    if (!product.image) {
      return '/static/placeholder-product.png';
    }
    
    if (typeof product.image === 'string') {
      const imageUrl = product.image.split(',')[0]?.trim();
      return imageUrl || '/static/placeholder-product.png';
    }
    
    if (Array.isArray(product.image)) {
      return product.image[0] || '/static/placeholder-product.png';
    }
    
    return '/static/placeholder-product.png';
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      const res = await apiService.delete(
        `${API_ENDPOINTS.DELETE_admin_Product}?product_id=${productToDelete.id}`
      );

      if (res.status === 200) {
        toast.showSuccess('Product deleted successfully!');
        if (onRefresh) onRefresh();
      } else {
        console.error('Delete failed:', res);
        toast.showError('Failed to delete the product.');
      }
    } catch (error) {
      console.error('⚠️ Error deleting product:', error);
      toast.showError('Error deleting product.');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  if (filteredProducts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No products found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {searchQuery ? `No products match "${searchQuery}"` : 'No products in this category'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {filteredProducts.map((product) => {
          const stockPercentage = getStockPercentage(product);
          const isExpanded = expandedProduct === product.id;
          const category = categories.find(c => c.id === product.category?.id);
          const { farmerPrice, mrp } = getProductDisplayPrices(product);

          return (
            <Grid item xs={12} sm={6} lg={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.15)}`
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={getProductImage(product)}
                  alt={product.name}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: alpha(theme.palette.grey[300], 0.3)
                  }}
                />

                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ 
                        fontSize: '1rem',
                        lineHeight: 1.3,
                        flex: 1,
                        minWidth: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.name}
                      </Typography>
                      <Tooltip title="View Stock">
                        <IconButton
                          size="small"
                          onClick={() => handleViewStock(product)}
                          sx={{
                            flexShrink: 0,
                            mt: -0.25,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.info.main, 0.2)
                            }
                          }}
                        >
                          <ViewStockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      SKU: {product.sku}
                    </Typography>

                    {category && (
                      <Chip
                        icon={<CategoryIcon />}
                        label={category.name}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" color="primary" fontWeight={700}>
                        {formatCurrency(farmerPrice)}
                      </Typography>
                      {mrp != null && mrp !== '' && Number(mrp) !== Number(farmerPrice) && (
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          {formatCurrency(mrp)}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      per {product.unit_of_measure}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1, backgroundColor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="h6" color="success.main" fontWeight={600}>
                          {product.analytics?.total_sold || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sold
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                        <Typography variant="h6" color="info.main" fontWeight={600}>
                          {product.analytics?.in_cart || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          In Cart
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {product.add_size === true && (
                      <Tooltip title="Add Size">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleAddSize(product)}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2)
                              }
                            }}
                          >
                            <StraightenIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}

                      <Tooltip title={!shouldEnableButton('add_stock') ? "You don't have permission to add stock" : "Add Stock"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleManageStock(product)}
                            disabled={!shouldEnableButton('add_stock')}
                            sx={{
                              backgroundColor: alpha(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.main,
                              opacity: !shouldEnableButton('add_stock') ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.warning.main, 0.2)
                              }
                            }}
                          >
                            <Inventory2Icon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Manage Images Button - Enabled when true, disabled when false */}
                      <Tooltip title={!shouldEnableButton('manage_image') ? "You don't have permission to manage images" : "Manage Images"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleImageManagement(product)}
                            disabled={!shouldEnableButton('manage_image')}
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              opacity: !shouldEnableButton('manage_image') ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.2)
                              }
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Edit Product Button - Enabled when true, disabled when false **/}
                      <Tooltip title={!shouldEnableButton('edit_product') ? "You don't have permission to edit product" : "Edit Product"}>
                        <span>
                          <IconButton
                            color="editGreen"
                            size="small"
                            onClick={() => onEditProduct && onEditProduct(product)}
                            disabled={!shouldEnableButton('edit_product')}
                            // sx={{
                            //   backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            //   color: theme.palette.primary.main,
                            //   opacity: !shouldEnableButton('edit_product') ? 0.5 : 1,
                            //   '&:hover': {
                            //     backgroundColor: alpha(theme.palette.primary.main, 0.2)
                            //   }
                            // }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Delete Product Button - Enabled when true, disabled when false */}
                      <Tooltip title={!shouldEnableButton('delete_product') ? "You don't have permission to delete product" : "Delete Product"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteDialog(product)}
                            disabled={!shouldEnableButton('delete_product')}
                            sx={{
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              color: theme.palette.error.main,
                              opacity: !shouldEnableButton('delete_product') ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.2)
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Button
                      size="small"
                      onClick={() => handleExpandClick(product.id)}
                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {isExpanded ? 'Less' : 'More'}
                    </Button>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.description || 'No description available'}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          HSN Code
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.hsn_code || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          GST Rate
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.gst}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Brand
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.brand?.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Vendor
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {product.vendor?.name || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {(product.ingredients && product.ingredients !== 'nan') && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Ingredients
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.ingredients}
                        </Typography>
                      </Box>
                    )}

                    {(product.shelf_life && product.shelf_life !== 'nan') && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Shelf Life
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.shelf_life}
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{productToDelete?.name || 'this product'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ImageManagement
        open={imageManagementOpen}
        onClose={() => setImageManagementOpen(false)}
        product={selectedProduct}
        onImageUpdated={onRefresh}
      />

      <AddProductSizeModal
        open={addSizeModalOpen}
        onClose={() => {
          setAddSizeModalOpen(false);
          setSizeProduct(null);
        }}
        product={sizeProduct}
        onSizeAdded={handleSizeUpdated}
      />

      <ManageProductSizesModal
        open={manageSizesModalOpen}
        onClose={() => {
          setManageSizesModalOpen(false);
          setSizeProduct(null);
        }}
        product={sizeProduct}
        onSizesUpdated={handleSizeUpdated}
      />

      <Dialog open={viewStockOpen} onClose={handleCloseViewStock} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {viewStockProduct?.add_size === true ? 'Available Stock by Size' : 'Available Stock'}
            </Typography>
            {viewStockProduct?.name && (
              <Typography variant="body2" color="text.secondary">
                {viewStockProduct.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseViewStock} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewStockLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : viewStockError ? (
            <Alert severity="error">{viewStockError}</Alert>
          ) : viewStockProduct?.add_size !== true ? (
            viewStockEntries.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No stock available.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {viewStockEntries.map((stockItem, index) => {
                  const available = stockItem.remaining_quantity ?? stockItem.available_quantity ?? stockItem.quantity ?? 0;
                  const isUnlimited = stockItem.is_unlimited === true
                    || stockItem.is_unlimited === 'true'
                    || stockItem.is_unlimited === 1;
                  const batchLabel = 'Stock';

                  return (
                    <Box
                      key={stockItem.id || index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1.5,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.04)
                      }}
                    >
                      <Typography variant="body1" fontWeight={600}>
                        {batchLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isUnlimited ? 'Unlimited' : `${available} available`}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )
          ) : viewStockSizes.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No stock available.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {viewStockSizes.map((sizeItem) => {
                const sizeLabel = `${sizeItem.size ?? ''} ${sizeItem.unit_of_measure || viewStockProduct?.unit_of_measure || ''}`.trim();
                const available = sizeItem.remaining_quantity ?? sizeItem.available_quantity ?? sizeItem.quantity ?? 0;
                const isUnlimited = sizeItem.is_unlimited === true
                  || sizeItem.is_unlimited === 'true'
                  || sizeItem.is_unlimited === 1;

                return (
                  <Box
                    key={sizeItem.id || sizeLabel}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.info.main, 0.04)
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {sizeLabel || 'Size'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isUnlimited ? 'Unlimited' : `${available} available`}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewStock} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsList;